<template>
  <div class="theme-switcher" :data-theme-root="theme.dark ? 'dark' : 'light'">
    <!-- 顶栏内主题切换项 -->
    <button class="theme-switcher-btn" type="button" title="切换主题" @click="togglePanel">
      <div class="theme-switcher-palette" aria-hidden="true">
        <i v-for="(c, idx) in theme.swatch" :key="idx" :style="{ background: c }"></i>
      </div>
      <span class="theme-switcher-btn-label">主题</span>
    </button>

    <!-- 展开面板 -->
    <transition name="ts-pop">
      <div v-if="open" class="theme-switcher-panel" @click.stop>
        <div class="theme-switcher-title">选择主题</div>
        <div class="theme-list">
          <button
            v-for="t in theme.THEME_LIST"
            :key="t.id"
            type="button"
            class="theme-item"
            :class="{ active: t.id === theme.currentId }"
            @click="pick(t.id)"
          >
            <span class="theme-swatch" :style="swatchStyle(t)">
              <i v-if="t.id === theme.currentId" class="theme-check">✓</i>
            </span>
            <span class="theme-meta">
              <span class="theme-name">{{ t.name }}</span>
              <span class="theme-desc">{{ t.desc }}</span>
            </span>
          </button>
        </div>
      </div>
    </transition>
  </div>

  <!-- 点击其它区域关闭 -->
  <div v-if="open" class="theme-switcher-overlay" @click="open = false"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useThemeStore } from '@/store/modules/theme'

const theme = useThemeStore()
const open = ref(false)

function togglePanel() { open.value = !open.value }
function pick(id) { theme.setTheme(id) }

function swatchStyle(t) {
  return {
    background: `linear-gradient(135deg, ${t.swatch[0]} 0%, ${t.swatch[0]} 45%, ${t.swatch[1]} 46%, ${t.swatch[1]} 100%)`
  }
}

function onEscape(e) { if (e.key === 'Escape') open.value = false }

onMounted(() => window.addEventListener('keydown', onEscape))
onUnmounted(() => window.removeEventListener('keydown', onEscape))
</script>

<style scoped>
.theme-switcher {
  /* 作为顶栏内行内项使用（不再 fixed 悬浮），由父级 header-right 布局定位 */
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.theme-switcher-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--color-border-light, rgba(0,184,212,0.2));
  background: var(--color-bg-raised, var(--color-bg-raised));
  cursor: pointer;
  transition: background var(--duration-fast, 140ms) var(--ease-standard), border-color var(--duration-fast, 140ms);
}
.theme-switcher-btn:hover {
  border-color: var(--color-border-active, rgba(0,184,212,0.3));
  background: var(--color-hover-bg, rgba(0,212,255,0.15));
}
.theme-switcher-palette {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  display: flex;
  overflow: hidden;
  outline: 1px solid var(--color-border-light, rgba(0,184,212,0.2));
  flex-shrink: 0;
}
.theme-switcher-palette i {
  flex: 1;
  display: block;
}
.theme-switcher-btn-label {
  font-size: 12px;
  color: var(--color-text-regular, #b2ebf2);
  white-space: nowrap;
}

.theme-switcher-overlay {
  position: fixed;
  inset: 0;
  z-index: 2900;
  background: transparent;
}

.theme-switcher-panel {
  /* 悬浮于顶栏下方右侧，避免被 header 的 overflow 裁剪 */
  position: fixed;
  top: 56px;
  right: 20px;
  width: 420px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid var(--color-border-light, rgba(0,184,212,0.2));
  background: var(--color-bg-raised, var(--color-bg-raised));
  box-shadow: var(--shadow-overlay, 0 12px 40px rgba(0,0,0,0.5));
  color: var(--color-text-primary, #e0f7fa);
  z-index: 3000;
}
.theme-switcher-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--color-text-primary, #e0f7fa);
}
.theme-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}
.theme-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 9px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background var(--duration-fast, 140ms), border-color var(--duration-fast, 140ms);
}
.theme-item:hover {
  background: var(--color-hover-bg, rgba(0,212,255,0.15));
}
.theme-item.active {
  border-color: var(--color-border-active, rgba(0,184,212,0.30));
  background: var(--color-hover-bg, rgba(0,212,255,0.15));
}
.theme-swatch {
  width: 40px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: 1px solid rgba(0,0,0,0.15);
  color: #fff;
  font-size: 14px;
}
.theme-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.theme-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary, #e0f7fa);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.theme-desc {
  font-size: 11px;
  color: var(--color-text-secondary, #80deea);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ts-pop-enter-active,
.ts-pop-leave-active {
  transition: opacity var(--duration-normal, 180ms) var(--ease-standard), transform var(--duration-normal, 180ms) var(--ease-standard);
}
.ts-pop-enter-from,
.ts-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
